import { Button, Dialog, Text, useVisibilityState } from '@lbcamden/lib-react';
import groupBy from 'lodash/groupBy';
import { FunctionComponent, useMemo } from 'react';
import { Link } from 'react-router';

import { personFieldDisplayValues } from '~/client/config/display-values/people';
import {
  useCurrentPeopleList,
  usePeopleAttributes,
  useReferenceListDisplayValue,
} from '~/client/hooks/storeHooks';
import { toPersonPage } from '~/client/utils/navigation';
import { mergePersonWithAttributes } from '~/common/utils/people';

import {
  PersonList,
  PersonSection,
  PersonSubSection,
  SupportNeedsNoticeRoot,
} from './SupportNeedsNotice.styled';

type Props = {
  propertyId: number;
};

const title = 'Resident support needs and adjustments';

export const SupportNeedsNotice: FunctionComponent<Props> = ({ propertyId }) => {
  const people = useCurrentPeopleList(propertyId);
  const peopleAttributes = usePeopleAttributes(propertyId);
  const referenceListDisplayValue = useReferenceListDisplayValue();

  const { visible, show, hide } = useVisibilityState();

  const attributesByPerson = useMemo(
    () => groupBy(peopleAttributes, 'personId'),
    [peopleAttributes],
  );

  const peopleWithAttributes = useMemo(
    () =>
      people
        .filter(({ personId }) => !!attributesByPerson[personId]?.length)
        .map((person) => mergePersonWithAttributes(person, attributesByPerson[person.personId])),
    [people, attributesByPerson],
  );

  if (!peopleWithAttributes.length) {
    return null;
  }

  return (
    <>
      <SupportNeedsNoticeRoot
        infoWithIcon
        title="Important"
        message={
          <>
            This property has residents with recorded support needs and/or adjustments.
            <Button $variant="link" $color="primary" onClick={show}>
              View details
            </Button>
          </>
        }
      />
      <Dialog open={visible} title={title} accessibleInfo={{ title }} onClose={hide}>
        <PersonList>
          {peopleWithAttributes.map(
            ({
              personId,
              fullName,
              supportNeeds,
              reasonableAdjustments,
              communicationAdjustments,
              communicationPreferences,
            }) => (
              <PersonSection key={personId}>
                <Text variant="h4">
                  <Link to={toPersonPage(propertyId, personId)}>{fullName}</Link>
                </Text>
                {(
                  [
                    [supportNeeds, personFieldDisplayValues.supportNeeds],
                    [reasonableAdjustments, personFieldDisplayValues.reasonableAdjustments],
                    [communicationAdjustments, personFieldDisplayValues.communicationAdjustments],
                    [communicationPreferences, personFieldDisplayValues.communicationPreferences],
                  ] as const
                ).map(
                  ([list, listTitle]) =>
                    !!list.length && (
                      <PersonSubSection key={listTitle}>
                        <Text variant="h5">{listTitle}</Text>
                        <ul>
                          {list
                            .map((item) =>
                              referenceListDisplayValue('PERSON_ATTRIBUTE', item.attributeCode),
                            )
                            .sort()
                            .map((value) => (
                              <li key={value}>{value}</li>
                            ))}
                        </ul>
                      </PersonSubSection>
                    ),
                )}
              </PersonSection>
            ),
          )}
        </PersonList>
      </Dialog>
    </>
  );
};
